import { useArticles } from '@dealstack/frontend/app/pages/HelpBlog/api/useArticles.ts';
import { useSingleCategory } from '@dealstack/frontend/app/pages/HelpBlog/api/useSingleCategory.ts';
import { ArticleCard } from '@dealstack/frontend/app/pages/HelpBlog/ArticleCard/ArticleCard.tsx';
import { CategoryPageContainer } from '@dealstack/frontend/app/pages/HelpBlog/CategoryPage/CategoryPage.styles.tsx';
import { makeDynamicHelpBlogUrl } from '@dealstack/frontend/app/pages/HelpBlog/help-blog.utils.ts';
import { HelpBlogLayout } from '@dealstack/frontend/app/pages/HelpBlog/HelpBlogLayout/HelpBlogLayout.tsx';
import { useHelpBlogBreadcrumbs } from '@dealstack/frontend/app/pages/HelpBlog/useHelpBlogBreadcrumbs.tsx';
import { HelpBlogContext } from '@dealstack/frontend/app/pages/HelpBlog/useHelpBlogController.tsx';
import { ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader.tsx';
import { QuickSwitchBreadcrumbsNav } from '@dealstack/frontend/shared/components/QuickSwitchBreadcrumbsNav/QuickSwitchBreadcrumbsNav.tsx';
import {
	useCurrentLanguage,
	useTranslationHelpBlog,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { HELP_BLOG_ROUTES } from '@dealstack/frontend/shared/shared-routes.ts';
import { DATE_FORMATS, formatStringDateToUI } from '@dealstack/shared/lib/format/dates/date.ts';
import { ArticleNameSlugField } from '@dealstack/shared/lib/generated';
import { Flex, Text, Title } from '@mantine/core';
import { useContext, useMemo } from 'react';
import { generatePath, Link, useNavigate, useParams } from 'react-router-dom';
import { ArticleCardProps } from '@dealstack/frontend/app/pages/HelpBlog/ArticleCard/ArticleCard.types';

export const CategoryPage = () => {
	const { dataset, perspective, mediaResolver } = useContext(HelpBlogContext);
	const tHelpBlog = useTranslationHelpBlog();
	const currentLanguage = useCurrentLanguage();
	const navigate = useNavigate();
	const { dealId } = useParams();

	const { slug } = useParams<{ slug: string }>();
	const { category, isFetching, error } = useSingleCategory(dataset, slug, perspective);
	const { articles } = useArticles(
		dataset,
		{
			enabled: !!category?.id,
			refId: category?.id || '',
			language: currentLanguage,
		},
		perspective
	);

	useHelpBlogBreadcrumbs([
		{
			name: tHelpBlog('breadcrumbs.help-title'),
			url: ROUTES.HELP_BLOG,
		},
		{
			name: tHelpBlog('breadcrumbs.help.categories'),
			url: `${ROUTES.HELP_BLOG}/${HELP_BLOG_ROUTES.CATEGORIES}`,
		},
		{
			name: category?.name || '',
			url: ROUTES.HELP_BLOG,
			noLink: true,
		},
	]);

	const articlesCount = articles?.length || 0;
	const createdAtDate = formatStringDateToUI(category?.createdAt, DATE_FORMATS.DATE_MONTH_YEAR);
	const metaInfoText = tHelpBlog('categoryPage.metaInfo');
	const categoryMetaInfo = `${articlesCount} ${metaInfoText} ${createdAtDate}`;

	const articleCards = useMemo<ArticleCardProps[]>(() => {
		return articles?.map((article) => {
			const publishedAt = formatStringDateToUI(
				article.createdAt,
				DATE_FORMATS.DATE_MONTH_YEAR
			);
			const metaContent = (
				<ArticleMeta
					dealId={dealId}
					tHelpBlog={tHelpBlog}
					author={article.author}
					publishedAt={publishedAt}
				/>
			);
			const articleUrl = makeDynamicHelpBlogUrl(
				dealId,
				`${ROUTES.HELP_BLOG}/${generatePath(HELP_BLOG_ROUTES.ARTICLE, {
					slug: article.slug.current,
				})}`
			);

			return {
				title: article.title,
				articleUrl,
				imageUrl: article?.image
					? mediaResolver(article.image).width(400).height(200).auto('format').url()
					: undefined,
				imageAlt: article.title,
				metaContent,
				previewText: article.previewSummary,
				mediaResolver,
			};
		});
	}, [articles, dealId, tHelpBlog]);

	if (isFetching) {
		return <FullscreenLoader name="pageLoading" />;
	}

	if (error || !category) {
		navigate(ROUTES.PAGE_NOT_FOUND);
		return null;
	}

	return (
		<HelpBlogLayout title={category.name}>
			<CategoryPageContainer>
				<div className="container">
					<div className="breadcrumbs">
						<QuickSwitchBreadcrumbsNav showQuickSwitch={false} />
					</div>

					<div className="header">
						<Title size="h1" fw={600} className="title">
							{category.name}
						</Title>

						<Text className="meta-text">{categoryMetaInfo}</Text>
					</div>

					<Flex className="articles" wrap="wrap" gap="2rem">
						{articleCards?.map((article, idx) => {
							return (
								<div key={articles[idx].id} className="card">
									<ArticleCard
										title={article.title}
										articleUrl={article.articleUrl}
										metaContent={article.metaContent}
										previewText={article.previewText}
										mediaResolver={article.mediaResolver}
									/>
								</div>
							);
						})}
					</Flex>
				</div>
			</CategoryPageContainer>
		</HelpBlogLayout>
	);
};

const ArticleMeta = ({
	dealId,
	tHelpBlog,
	author,
	publishedAt,
}: {
	dealId: string | undefined;
	tHelpBlog: ReturnType<typeof useTranslationHelpBlog>;
	author: ArticleNameSlugField;
	publishedAt: string | undefined;
}) => {
	const byText = tHelpBlog('category.article.card.by');
	const authorUrl = makeDynamicHelpBlogUrl(
		dealId,
		`${ROUTES.HELP_BLOG}/${generatePath(HELP_BLOG_ROUTES.AUTHOR, {
			slug: author.slug.current || '',
		})}`
	);

	return (
		<>
			{byText} <Link to={authorUrl}>{author.name}</Link> • {publishedAt}
		</>
	);
};
